import React, {useState, useRef, useContext, useEffect} from "react"
import {useParams} from "react-router-dom"
import {DataTable} from "primereact/datatable"
import {Column} from "primereact/column"

import AddFacilityModal from "../../components/AddFacilityModal"
import CenterLoader from "../../components/CenterLoader"
import {Main, FiltersTable} from "../../components/Layout"
import SubHeader from "../../components/SubHeader"

import {bulkFilter, isJSON} from "../../utilities/general"
import {wrappedGet, wrappedDelete, wrappedPut} from "../../utilities/AxiosHelper"
import RefContext from "../../utilities/RefContext"
import {useStableNavigate} from "../../utilities/StableNavigateContext"
import {getCellCustom, getExportableField} from "../../utilities/admin"
import UploadButton from "../../components/UploadButton"
import JsonEditor from "../../components/JsonEditor"

/**
 * Organization Component
 * Renders the organization view
 */
const Organization = () => {
    const {organizationId} = useParams()
    const tableRef = useRef(null)
    const {dispatch} = useContext(RefContext)

    const [org, setOrg] = useState({name: "", active: true, customerKey: ""})
    const [searchText, setSearchText] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [facilities, setFacilities] = useState([])
    const [filteredFacilities, setFilteredFacilities] = useState([])
    const navigate = useStableNavigate()
    const [showModal, setShowModal] = useState(false)
    const [isJsonValid, setIsJsonValid] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        fetchAll()
    }, [])

    useEffect(() => {
        if (org.name !== "") {
            document.title = `Organization - ${org.name}`
        }
    }, [org])

    const fetchAll = () => {
        Promise.all([fetchOrgData(), fetchOrgFacilityData()])
            .catch(() => {
                // optional: global error handling if you want
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const fetchOrgData = () => {
        return wrappedGet(`organization/${organizationId}`)
            .then((response) => {
                const data = response.data
                if (!data) return

                let customerKey = ""
                if (data.customer_key !== undefined && data.customer_key !== null) {
                    customerKey = data.customer_key
                }
                const config = JSON.stringify(data.config, null, 2)

                setIsJsonValid(isJSON(config))

                setOrg({name: data.name, active: data.active, customerKey, config})
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Organization",
                    timeout: 5000,
                })
            })
    }

    const fetchOrgFacilityData = () => {
        return wrappedGet(`organization/${organizationId}/facility`)
            .then((response) => {
                const results = response.data?.results

                if (Array.isArray(results) && results.length > 0) {
                    setFacilities(results)
                    setFilteredFacilities(results)
                }
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Organization Facilities",
                    timeout: 5000,
                })
            })
    }

    const editFacility = (selectedRow) => {
        navigate(`/admin/organization/${organizationId}/facility/${selectedRow.id}`)
    }

    const handleReset = () => {
        setSearchText("")
    }

    const deleteFacility = (facility) => {
        let confirmAction = window.confirm("Are you sure you want to disable this facility?")
        if (confirmAction) {
            wrappedDelete(`facility/${facility.id}`)
                .then((response) => {
                    if (response.status === 200) {
                        dispatch("showToast", {
                            show: true,
                            variant: "success",
                            text: "Facility is disabled",
                            timeout: 5000,
                        })
                    } else {
                        dispatch("showToast", {
                            show: true,
                            variant: "danger",
                            text: "Error disabling facility",
                            timeout: 5000,
                        })
                        return
                    }
                })
                .catch((error) => {
                    dispatch("showToast", {
                        show: true,
                        variant: "danger",
                        text: "Error disabling facility",
                        timeout: 5000,
                    })
                    return
                })
        }
    }

    const columns = [
        {header: "Name", field: "name", export: true},
        {header: "Short Name", field: "shortname", export: true},
        {header: "Customer Key", field: "customer_key", export: true},
        {header: "Active", field: "active", export: true},
        {header: "Created At", field: "created_at", export: true},
        {header: "Updated At", field: "updated_at", export: true},
        {header: "Actions", field: "actions", export: false},
    ]

    const addFacility = () => {
        setShowModal(true)
    }

    const hideModal = () => {
        setShowModal(false)
        setIsLoading(true)
        fetchAll()
    }

    const handleSave = () => {
        setIsLoading(true)
        // save the config as json
        org.config = JSON.parse(org.config)
        
        wrappedPut(`organization/${organizationId}`, org)
            .then((response) => {
                org.config = JSON.stringify(org.config, null, 2)
                dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "Facility Updated",
                    timeout: 5000,
                })
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "error",
                    text: "Problem fetching Facility Units",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleBack = () => {
        navigate("/admin/organization")
    }

    if (isLoading) {
        return <CenterLoader />
    }

    const helpContent = (
        <>
            <h4>Instructions</h4>
            <p>Bulk uploading Facilties requires a CSV file with the following fields</p>

            <ol>
                <li>
                    <strong>customer_key:</strong> a unique identifier used by your organization. This will be used if
                    you would like to update this facility. This field must not exceed 255 characters
                </li>
                <li>
                    <strong>name:</strong> The name of your facility
                </li>
                <li>
                    <strong>shortname:</strong> any shortname for your facility
                </li>
                <li>
                    <strong>active:</strong> not required but can be specified as a boolean as either 'TRUE' or 'FALSE'
                </li>
            </ol>
            <h5>Example CSV File</h5>
            <pre>
                customer_key,name,shortname,active
                <br />
                239382,Facility 1,Fac1,FALSE
            </pre>
        </>
    )

    return (
        <>
            <SubHeader title1="Organization" title2={org.name} icon="fa-sitemap">
                <div className="d-flex flex-grow-1 justify-content-end">
                    <div className="d-flex flex-grow-1 justify-content-end">
                        <button className="btn btn-secondary fw-semibold me-2 px-6" onClick={handleBack}>
                            <i className="fas fa-arrow-left"></i>Back
                        </button>
                        <button className="btn btn-primary fw-semibold me-2 px-6" onClick={addFacility}>
                            <i className="fas fa-plus"></i>Add Facility
                        </button>

                        <UploadButton
                            uploadUrl={`bulk/upload/${organizationId}/facilites`}
                            label="Bulk Load Facilites"
                            dispatch={dispatch}
                            reload={fetchAll}
                            helpTitle="Bulk Upload Facilities"
                            helpContent={helpContent}
                        />
                    </div>
                </div>
            </SubHeader>
            <Main>
                <div className="content d-flex flex-column flex-column-fluid">
                    <div className="container-fluid">
                        <div className="py-3 py-lg-6">
                            <div className="card">
                                <div className="col-xxl-12">
                                    <div className="row g-8 p-8 d-flex flex-row align-items-end">
                                        <div className="col">
                                            <div className="row g-8 align-items-end">
                                                <div className="col-4">
                                                    <label htmlFor="id-org-name" className="form-label fw-bold">
                                                        Name:
                                                    </label>
                                                    <input
                                                        id="id-org-name"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Name"
                                                        value={org.name}
                                                        onChange={(evt) => {
                                                            setOrg((prev) => ({
                                                                ...prev,
                                                                name: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label htmlFor="id-org-shortname" className="form-label fw-bold">
                                                        Short Name:
                                                    </label>
                                                    <input
                                                        id="id-org-shortname"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Short Name"
                                                        value={org.shortname}
                                                        onChange={(evt) => {
                                                            setOrg((prev) => ({
                                                                ...prev,
                                                                shortname: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label htmlFor="id-org-customer-key" className="form-label fw-bold">
                                                        Customer Key:
                                                    </label>
                                                    <input
                                                        id="id-org-customer-key"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Key"
                                                        value={org.customerKey}
                                                        onChange={(evt) => {
                                                            setOrg((prev) => ({
                                                                ...prev,
                                                                customerKey: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label htmlFor="id-org-active" className="form-label fw-bold">
                                                        Active:
                                                    </label>
                                                    <select
                                                        className="form-select form-select-solid fw-bold"
                                                        id="id-org-active"
                                                        checked={org.active}
                                                        onChange={(evt) => {
                                                            setOrg((prev) => ({
                                                                ...prev,
                                                                active: evt.target.value === "true",
                                                            }))
                                                        }}
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                </div>
                                                <div className="col-4">
                                                    <label htmlFor="id-org-active" className="form-label fw-bold">
                                                        Config:
                                                    </label>
                                                    <JsonEditor
                                                        data={org.config}
                                                        onDataChange={(data) => {
                                                            setOrg((prev) => ({
                                                                ...prev,
                                                                config: data,
                                                            }))
                                                        }}
                                                        isDataValid={(isValid) => {
                                                            setIsJsonValid(isValid)
                                                        }}
                                                    />
                                                </div>

                                                <div className="col-lg-2 col-md-6">
                                                    <div className="d-flex justify-content-start">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-secondary fw-semibold me-2 px-6"
                                                            disabled={!isJsonValid}
                                                            onClick={handleSave}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <FiltersTable
                    Filters={
                        <div className="row g-8 align-items-end">
                            <div className="col-12">
                                <h3>Facilities</h3>
                            </div>
                            <div className="col-lg-2 col-md-12">
                                <label htmlFor="id-search-users" className="form-label fw-bold">
                                    Search Facilites:
                                </label>
                                <div className="d-flex align-items-center position-relative">
                                    <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                                    <input
                                        id="id-search-users"
                                        type="text"
                                        className="form-control form-control-solid ps-12"
                                        placeholder="Name"
                                        value={searchText}
                                        onChange={(evt) => {
                                            setSearchText(evt.target.value)
                                            const filteredFacilities = bulkFilter(facilities, [
                                                [["name"], evt.target.value, "inexact"],
                                            ])
                                            setFilteredFacilities(filteredFacilities)
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6">
                                <div className="d-flex justify-content-start">
                                    <button
                                        type="reset"
                                        className="btn btn-secondary fw-semibold me-2 px-6"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                    Table={
                        <DataTable
                            ref={tableRef}
                            value={filteredFacilities}
                            tableClassName="table gy-3 gs-5 rounded w-100 mt-3 align-middle"
                            paginator
                            rows={25}
                            sortField={columns[0].field}
                            sortOrder={1}
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            rowHover={true}
                            onMouseDownCapture={(e) => {
                                e.stopPropagation()
                            }}
                        >
                            {columns.map((colDef, index) => (
                                <Column
                                    key={index}
                                    field={colDef.field}
                                    header={colDef.header}
                                    headerClassName="th-sorting-center align-middle text-start text-light text-uppercase bg-gray-700 fs-7 fw-bold p-sortable-column"
                                    sortable
                                    body={(row) => getCellCustom(colDef, row, editFacility, deleteFacility, false)}
                                    exportable={colDef.export}
                                    exportField={(row) => getExportableField(colDef, row)}
                                />
                            ))}
                        </DataTable>
                    }
                />
            </Main>
            <AddFacilityModal showModal={showModal} closeModal={hideModal} organizationId={organizationId} />
        </>
    )
}

export default Organization
